import { motion } from 'framer-motion';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import FormControl from 'react-bootstrap/FormControl';
import FormGroup from 'react-bootstrap/FormGroup';
import Row from 'react-bootstrap/Row';
import { FaFilter } from 'react-icons/fa';
import Pagination from 'react-js-pagination';
import { RouteComponentProps } from 'react-router-dom';
import Select from 'react-select';
import {
  encodeQueryParams, NumberParam,
  StringParam,
  useQueryParams
} from 'use-query-params';
import { Artist, EventArtist, EventShow, FreqShoEvent } from '../../interfaces';
import { ArtistVideo } from '../../interfaces/AritstVideo';
import {
  fetchArtistVideos, fetchEvent,
  fetchEventArtists,
  fetchEventShows,
  fetchGenres
} from '../api';
import DataList from '../components/DataList';
import DataTable from '../components/DataTable';
import Header from '../components/Header';
import Loading from '../components/Loading';
import RatingModal from '../components/RatingModal';
import StyledButton from '../components/StyledButton';
import StyledSelect from '../components/StyledSelect';
import StyledSwitch from '../components/StyledSwitch';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { UserArtistRatingsContext } from '../state/UserArtistRatings';
import { DataItemState } from '../types';
import { mapEventArtistsDisplay } from '../util/mapEventArtistsDisplay';
import { mapEventShowsDisplay } from '../util/mapEventShowsDisplay';

type MatchParams = {
  id: string;
};

const DisplayEvent = ({ match }: RouteComponentProps<MatchParams>) => {
  const [isLoading, setIsLoading] = useState(true);

  const [userArtistRatings, setUserArtistRatings] = useState();

  const [eventData, setEventData]: DataItemState<FreqShoEvent> = useState();
  const [eventDataItems, setEventDataItems] = useState([]);
  const [eventDisplayItems, setEventDisplayItems]: any[] = useState();

  const [genres, setGenres]: DataItemState<
    { id: number; name: string }[]
  > = useState();

  const [
    artistBeingRated,
    setArtistBeingRated,
  ]: DataItemState<Artist> = useState();

  const [artistVideos, setArtistVideos]: DataItemState<
    ArtistVideo[]
  > = useState();

  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    display: StringParam,
    freqshorecommends: NumberParam,
    name: StringParam,
    freqgenres: StringParam,
    ratingIndex: NumberParam,
    order: StringParam,
  });

  useEffect(() => {
    if (!query.display) {
      setQuery({ display: 'Artists', page: undefined })
    }
  }, [query])

  const [resultCount, setResultCount]: DataItemState<number> = useState();

  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const breakpoint = useBreakpoint();

  // init
  useEffect(() => {
    async function initialize() {
      const { id } = match.params;
      const { display } = query;
      const data = await fetchEvent(id);
      const genres = await fetchGenres();
      setEventData(data);
      setGenres(genres);

      const queryString = stringify(
        encodeQueryParams(
          {
            page: NumberParam,
            display: StringParam,
            freqshorecommends: NumberParam,
            name: StringParam,
            order: StringParam,
          },
          query
        )
      );
      if (display === 'Artists') {
        const dataItems = await fetchEventArtists(id, queryString);
        setEventDataItems(dataItems.results);

        const displayFormattedItems = mapEventArtistsDisplay(
          (dataItems.results as any) as EventArtist[],
          onArtistRateClick
        );
        setEventDisplayItems(displayFormattedItems);
        setResultCount(dataItems.count);
        setIsLoading(false);
      } else if (display === 'Shows') {
        const dataItems = await fetchEventShows(id, queryString);
        setEventDataItems(dataItems.results);
        const displayFormattedItems = mapEventShowsDisplay(
          (dataItems.results as any) as EventShow[],
          onArtistRateClick
        );
        setEventDisplayItems(displayFormattedItems);
        setResultCount(dataItems.count);
        setIsLoading(false);
      }
    }

    initialize();
  }, []);

  // all query param changes except ratingIndex
  useEffect(() => {
    if (!query) return;

    async function update() {
      setIsLoading(true);

      const { id } = match.params;
      const { display } = query;
      const queryString = stringify(
        encodeQueryParams(
          {
            page: NumberParam,
            display: StringParam,
            freqshorecommends: NumberParam,
            name: StringParam,
            order: StringParam
          },
          query
        )
      );
      if (display === 'Artists') {
        const dataItems = await fetchEventArtists(id, queryString);
        const userRatings = dataItems.results.map((x) => {
          return { id: x.artist.id, score: x.artist.myrating.score };
        });
        setUserArtistRatings(userRatings);
        setEventDataItems(dataItems.results);
        const displayFormattedItems = mapEventArtistsDisplay(
          (dataItems.results as any) as EventArtist[],
          onArtistRateClick
        );
        setEventDisplayItems(displayFormattedItems);
        setResultCount(dataItems.count);
        setIsLoading(false);
      } else if (display === 'Shows') {
        const dataItems = await fetchEventShows(id, queryString);
        const userRatings = dataItems.results.map((x) => {
          return { id: x.artist.id, score: x.artist.myrating.score };
        });
        setUserArtistRatings(userRatings);
        setEventDataItems(dataItems.results);
        const displayFormattedItems = mapEventShowsDisplay(
          (dataItems.results as any) as EventShow[],
          onArtistRateClick
        );
        setEventDisplayItems(displayFormattedItems);
        setResultCount(dataItems.count);
        setIsLoading(false);
      }
    }

    update();
  }, [
    query.page,
    query.display,
    query.freqshorecommends,
    query.name,
    query.freqgenres,
    query.order,
  ]);

  // selecting an artist to rate
  useEffect(() => {
    if (query.ratingIndex === undefined || !eventDataItems) return;
    if (query.ratingIndex === undefined) {
      setArtistVideos(undefined);
      return;
    }

    async function getArtistData() {
      const index = query.ratingIndex as number;
      if (!(eventDataItems as any)[index]) return;
      const artist = (eventDataItems as any)[index].artist;
      // const artistData = (await fetchArtist(artist.id));
      const artistVideos = (await fetchArtistVideos(artist.id)).data;
      setArtistBeingRated(artist);
      setArtistVideos(artistVideos.videos);
    }

    if (query.ratingIndex !== undefined) getArtistData();
  }, [query.ratingIndex, eventDisplayItems]);

  useEffect(() => {
    if (
      !userArtistRatings ||
      !eventDataItems ||
      !query ||
      query.ratingIndex !== undefined
    )
      return;
    async function update() {
      setIsLoading(true);
      const { id } = match.params;
      const { display } = query;
      const queryString = stringify(
        encodeQueryParams(
          {
            page: NumberParam,
            display: StringParam,
            freqshorecommends: NumberParam,
            name: StringParam,
            order: StringParam,
          },
          query
        )
      );
      if (display === 'Artists') {
        const dataItems = await fetchEventArtists(id, queryString);
        setEventDataItems(dataItems.results);
        const displayFormattedItems = mapEventArtistsDisplay(
          (dataItems.results as any) as EventArtist[],
          onArtistRateClick
        );
        setEventDisplayItems(displayFormattedItems);
        setIsLoading(false);
      } else if (display === 'Shows') {
        const dataItems = await fetchEventShows(id, queryString);
        setEventDataItems(dataItems.results);
        const displayFormattedItems = mapEventShowsDisplay(
          (dataItems.results as any) as EventShow[],
          onArtistRateClick
        );
        setEventDisplayItems(displayFormattedItems);
        setIsLoading(false);
      }
    }
    update();
  }, [query.ratingIndex]);


  useEffect(() => {
    setQuery({ page: undefined })
  }, [
    query.display,
    query.freqshorecommends,
    query.name,
    query.freqgenres,
    query.order
  ])

  const onArtistRateClick = (index: number) => {
    setQuery({ ratingIndex: index });
  };

  const onPreviousArtistClick = () => {
    if (!eventDataItems) return;
    const prevIndex = query.ratingIndex - 1;
    if (prevIndex < 0) {
      if (!query.page || query.page === 1) return;
      setQuery({
        ratingIndex: (eventDataItems as any).length - 1,
        page: (query.page || 1) - 1,
      });
    } else {
      setQuery({ ratingIndex: prevIndex });
    }
  };

  const onNextArtistClick = () => {
    if (!eventDataItems) return;
    const nextIndex = query.ratingIndex + 1;
    if (nextIndex > (eventDataItems as any).length - 1) {
      setQuery({ ratingIndex: 0, page: (query.page || 1) + 1 });
    } else {
      setQuery({ ratingIndex: nextIndex });
    }
  };

  const setHighestRatingSort = (val: boolean) => {
    if (val) {
      setQuery({ order: '-myrating,-artist__freqscore' });
    } else {
      setQuery({ order: undefined });
    }
  }
  if (!eventData || !eventDataItems) {
    return (
      <div className="justify-content-center align-items-center" style={{ paddingTop: '40vh' }}>
        <Loading />
      </div>
    )
  }

  return (
    <div>
      <UserArtistRatingsContext.Provider
        value={[userArtistRatings, setUserArtistRatings]}
      >
        <RatingModal
          videos={artistVideos}
          ratingIndex={query.ratingIndex}
          setQuery={setQuery}
          artistBeingRated={artistBeingRated}
          onPrevious={onPreviousArtistClick}
          onNext={onNextArtistClick}
        />
        {eventData && (
          <Header
            filterDesktop={
              <React.Fragment>
                <StyledButton
                  className="align-items-center justify-content-center small mr-2"
                  variant="secondary"
                  onClick={() => setAreFiltersOpen((prevState) => !prevState)}
                >
                  <FaFilter size={20} />
                  <span className="my-0 py-0 ml-2">
                    {areFiltersOpen ? 'Hide' : 'View'} Filters
                  </span>
                </StyledButton>
                <Badge pill variant="secondary" className="ml-4">
                  <div className="px-2 py-1 m-0">{resultCount} Results</div>
                </Badge>
              </React.Fragment>
            }
            title={eventData.name + ' ' + query.display}
            select={
              <StyledSelect
                value={query.display as string}
                options={['Artists', 'Shows']}
                onChange={(display) => setQuery({ display, page: undefined })}
              />
            }
          />
        )}
        {(breakpoint === 'xs' ||
          breakpoint === 'sm' ||
          breakpoint === 'md' ||
          breakpoint === 'lg') && (
            <React.Fragment>
              <motion.div
                variants={MobileFilterVariants}
                animate={areFiltersOpen ? 'open' : 'closed'}
                style={{ height: areFiltersOpen ? 'auto' : 0 }}
                className={areFiltersOpen ? 'mt-4 mb-3' : ''}
              // initial={{x: '-150%' }}
              >
                <div>
                  <div className="pb-1">
                    <form>
                      <FormGroup role="form">
                        <FormControl
                          placeholder="Search by Artist"
                          type="text"
                          className="form-control"
                          value={query.name || ''}
                          onChange={(e) => {
                            const name = e.target.value;
                            if (!name || name === '') {
                              setQuery({ name: undefined, page: undefined });
                            } else {
                              setQuery({ name: e.target.value, page: undefined });
                            }
                          }}
                        />
                      </FormGroup>
                    </form>
                  </div>

                  <div className="pb-3">
                    {genres && (
                      <Select
                        placeholder="Search by Genre"
                        className="multi-select"
                        isMulti
                        onChange={(e: any) => {
                          const genresString = e
                            ?.map((genre: any) => genre.value)
                            .join(',');
                          setQuery({
                            freqgenres: genresString || undefined,
                            page: undefined,
                          });
                        }}
                        options={genres?.map((x) => {
                          return { label: x.name, value: x.id };
                        })}
                      />
                    )}
                  </div>
                  <div className="pb-3">
                    <StyledSwitch
                      label="FreqSho Recommends"
                      onChange={(val: boolean) =>
                        setQuery({
                          freqshorecommends: val ? 1 : undefined,
                          page: undefined,
                        })
                      }
                      checked={!!query.freqshorecommends}
                    />
                  </div>

                  <div className="pb-4">
                    <StyledSwitch
                      label={`My Highest Rated ${query.display}`}
                      onChange={(val: boolean) =>
                        setHighestRatingSort(val)
                      }
                      checked={query.order === '-myrating,-artist__freqscore'}
                    />
                  </div>
                  <div className="pb-0">
                    <Button
                      onClick={() =>
                        setQuery({
                          page: undefined,
                          freqshorecommends: undefined,
                          freqgenres: undefined,
                          name: undefined,
                          order: undefined,
                        })
                      }
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </motion.div>

              {eventDisplayItems && <DataList items={eventDisplayItems} isLoading={isLoading} />}
            </React.Fragment>
          )}

        {breakpoint === 'xl' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <motion.div
              style={{
                visibility: areFiltersOpen ? 'visible' : 'hidden',
              }}
              variants={FilterVariants}
              initial={areFiltersOpen ? 'expanded' : 'collapsed'}
              animate={areFiltersOpen ? 'expanded' : 'collapsed'}
            >
              <div className="pr-4">
                <div className="pb-3">
                  <form>
                    <FormGroup role="form">
                      <FormControl
                        placeholder="Search by Artist"
                        type="text"
                        className="form-control"
                        value={query.name || ''}
                        onChange={(e) => {
                          const name = e.target.value;
                          if (!name || name === '') {
                            setQuery({ name: undefined, page: undefined });
                          } else {
                            setQuery({ name: e.target.value, page: undefined });
                          }
                        }}
                      />
                    </FormGroup>
                  </form>
                </div>

                <div className="pb-3">
                  {genres && (
                    <Select
                      placeholder="Search by Genre"
                      className="multi-select"
                      isMulti
                      onChange={(e: any) => {
                        const genresString = e
                          ?.map((genre: any) => genre.value)
                          .join(',');
                        setQuery({
                          freqgenres: genresString || undefined,
                          page: undefined,
                        });
                      }}
                      options={genres?.map((x) => {
                        return { label: x.name, value: x.id };
                      })}
                    />
                  )}
                </div>
                <div className="pb-3">
                  <StyledSwitch
                    label="FreqSho Recommends"
                    onChange={(val: boolean) =>
                      setQuery({
                        freqshorecommends: val ? 1 : undefined,
                        page: undefined,
                      })
                    }
                    checked={!!query.freqshorecommends}
                  />
                </div>

                <div className="pb-4">
                  <StyledSwitch
                    label={`My Highest Rated ${query.display}`}
                    onChange={(val: boolean) =>
                      setHighestRatingSort(val)
                    }
                    checked={query.order === '-myrating,-artist__freqscore'}
                  />
                </div>
                <div className="pb-0">
                  <Button
                    onClick={() =>
                      setQuery({
                        page: undefined,
                        freqshorecommends: undefined,
                        freqgenres: undefined,
                        name: undefined,
                        order: undefined,
                      })
                    }
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </motion.div>

            {eventDisplayItems && (
              <DataTable items={eventDisplayItems} isLoading={isLoading} />
            )}
          </div>
        )}

        {!!resultCount && (
          <Container fluid className="mt-4">
            <Row>
              <Col>
                <Pagination
                  disabledClass="pagination-disabled"
                  itemClass="page-item"
                  linkClass="page-link"
                  activePage={query.page || 1}
                  itemsCountPerPage={20}
                  totalItemsCount={resultCount}
                  pageRangeDisplayed={5}
                  onChange={(page: number) => setQuery({ page })}
                />
              </Col>
            </Row>
          </Container>
        )}
      </UserArtistRatingsContext.Provider>
    </div>
  );
};

export default DisplayEvent;

const FilterVariants = {
  expanded: () => ({
    width: 600,
  }),
  collapsed: () => ({
    width: 0,
  }),
};

const MobileFilterVariants = {
  open: { opacity: 1, x: 0 },
  closed: { opacity: 0, x: '-100%' },
};
